import { movies, type Movie } from "@/mocks/movies";
import { reviews, type Review } from "@/mocks/reviews";

export const PAGE_SIZE = 6;

export function getMovieById(id?: string): Movie | undefined {
  if (!id) return undefined;
  return movies.find((movie) => movie.id === id);
}

export function getReviewById(id?: string): Review | undefined {
  if (!id) return undefined;
  return reviews.find((review) => review.id === id);
}

export function getMovieByReview(review: Review): Movie | undefined {
  return getMovieById(review.movieId);
}

export function getReviewsByMovie(movieId: string): Review[] {
  return reviews.filter((review) => review.movieId === movieId);
}

export function getReviewExcerpts(movieId: string, limit = 3): Review[] {
  const own = getReviewsByMovie(movieId);
  const movie = getMovieById(movieId);
  if (!movie) return own.slice(0, limit);
  const related = reviews.filter(
    (review) =>
      review.movieId !== movieId &&
      (getMovieById(review.movieId)?.genres ?? []).some((genre) => movie.genres.includes(genre)),
  );
  return [...own, ...related].slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  return movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const genreScore = item.genres.filter((genre) => movie.genres.includes(genre)).length * 10;
      const yearScore = Math.max(0, 6 - Math.abs(item.year - movie.year));
      return { item, score: genreScore + yearScore };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getFeaturedMovies(limit = 3): Movie[] {
  const featured = movies.filter((movie) => movie.featured);
  return (featured.length ? featured : movies).slice(0, limit);
}

export function getLatestMovies(limit = 6): Movie[] {
  return [...movies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export type SortKey = "updated" | "rating" | "views";

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const sorted = [...list];
  if (sort === "rating") {
    return sorted.sort((a, b) => b.rating - a.rating);
  }
  if (sort === "views") {
    return sorted.sort((a, b) => b.views - a.views);
  }
  return sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function getPopularReviews(limit = 5): Review[] {
  return [...reviews].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatestReviews(limit = 3): Review[] {
  return [...reviews]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((movie) =>
    [movie.title, movie.enTitle, movie.summary, movie.director, ...movie.cast, ...movie.genres]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
}

export function searchReviews(keyword: string): Review[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return reviews.filter((review) =>
    [review.title, review.excerpt, review.author, ...review.content, ...review.tags]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
}

export function matchesMovieKeyword(movie: Movie, keyword: string): boolean {
  const q = keyword.trim().toLowerCase();
  if (!q) return true;
  return [movie.title, movie.enTitle, movie.summary, ...movie.genres]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function formatViews(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)} 万`;
  }
  return `${value}`;
}

export function formatDate(value: string): string {
  return value.replace(/-/g, " / ");
}

export function paginate<T>(list: T[], page: number, size = PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(list.length / size));
  const current = Math.min(Math.max(1, page), totalPages);
  return {
    totalPages,
    current,
    items: list.slice((current - 1) * size, current * size),
  };
}